(() => {
  'use strict';
  const labels = window.prandyLabels || {};
  const root = () => window.Shopify?.routes?.root || '/';
  const money = cents => new Intl.NumberFormat(document.documentElement.lang || 'es-MX', {style:'currency', currency:window.Shopify?.currency?.active || 'MXN'}).format(cents / 100);
  const say = (key, amount) => (labels[key] || '').replace('%amount%', amount);
  class PrandyMobilePurchase extends HTMLElement {
    connectedCallback() {
      this.info = document.querySelector('product-info');
      if (!this.info) return;
      this.button = this.querySelector('[data-sticky-add]');
      this.onClick = () => {
        const native = this.info.querySelector('button[name="add"]');
        if (!native || native.disabled) return;
        native.click();
      };
      this.button.addEventListener('click', this.onClick);
      this.refresh = () => {
        const native = this.info.querySelector('button[name="add"]');
        if (!native) { this.hidden = true; return; }
        this.button.disabled = native.disabled;
        this.button.textContent = native.textContent.trim();
        const price = this.info.querySelector('.price:not(.price--on-sale) .price-item--regular, .price--on-sale .price-item--sale');
        if (price) this.querySelector('[data-sticky-price]').textContent = price.textContent.trim();
        const box = native.getBoundingClientRect();
        const frame = document.getElementById('PBarNextFrameWrapper') || document.getElementById('preview-bar-iframe');
        this.style.bottom = (frame ? Math.max(0, frame.getBoundingClientRect().height) : 0) + 'px';
        if (frame && !this.previewObserver) {
          this.previewObserver = new ResizeObserver(this.refresh);
          this.previewObserver.observe(frame);
        }
        this.hidden = !window.matchMedia('(max-width: 749px)').matches || (box.top >= 0 && box.bottom <= window.innerHeight);
      };
      this.observer = new MutationObserver(this.refresh);
      this.observer.observe(this.info, {subtree:true, childList:true, attributes:true, attributeFilter:['disabled']});
      window.addEventListener('scroll', this.refresh, {passive:true});
      window.addEventListener('resize', this.refresh);
      this.refresh();
      this.previewTimers = [500,1500,3000].map(delay => setTimeout(this.refresh, delay));
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.previewObserver?.disconnect();
      this.previewTimers?.forEach(clearTimeout);
      window.removeEventListener('scroll', this.refresh);
      window.removeEventListener('resize', this.refresh);
      this.button?.removeEventListener('click', this.onClick);
    }
  }
  class PrandyCartBenefits extends HTMLElement {
    connectedCallback() {
      this.cart = JSON.parse(this.dataset.cart || '{}');
      this.update();
      this.onClick = event => {
        if (event.target.closest('[data-claim-gift]')) this.claim();
        if (event.target.closest('[data-quote-shipping]')) this.quote();
      };
      this.onChange = event => {
        if (event.target.matches('[data-dog-confirm]')) this.querySelector('[data-claim-gift]').disabled = !event.target.checked;
      };
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
    }
    disconnectedCallback() {
      this.addEventListener && this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      this.abort?.abort();
    }
    update() {
      const cart = this.cart;
      const total = cart.total_price || 0;
      const gifts = (cart.items || []).filter(item => String(item.variant_id) === this.dataset.giftId);
      const eligible = total - gifts.reduce((sum, item) => sum + item.final_line_price, 0);
      this.querySelector('[data-shipping-progress]').textContent = total >= 99900 ? labels.shippingReached : say('shippingRemaining', money(99900 - total));
      this.querySelector('[data-shipping-bar]').value = Math.min(total, 99900);
      const status = this.querySelector('[data-gift-status]');
      const claim = this.querySelector('[data-gift-claim]');
      claim.hidden = true;
      if (gifts.some(item => item.final_line_price === 0)) status.textContent = labels.giftClaimed;
      else if (gifts.length) status.textContent = labels.giftPaid;
      else if (eligible < 50000) status.textContent = say('giftRemaining', money(50000 - eligible));
      else if (this.dataset.giftAvailable !== 'true') status.textContent = labels.giftUnavailable;
      else { status.textContent = labels.giftEligible; claim.hidden = false; }
    }
    async claim() {
      const button = this.querySelector('[data-claim-gift]');
      const message = this.querySelector('[data-cart-message]');
      if (!this.querySelector('[data-dog-confirm]').checked || button.disabled) return;
      button.disabled = true;
      message.textContent = labels.adding;
      try {
        const fresh = await fetch(root() + 'cart.js').then(response => response.json());
        if (fresh.items.some(item => String(item.variant_id) === this.dataset.giftId)) { this.cart = fresh; this.update(); message.textContent = ''; return; }
        if (fresh.total_price < 50000) throw new Error(labels.giftChanged);
        const response = await fetch(root() + 'cart/add.js', {method:'POST',headers:{'Content-Type':'application/json','Accept':'application/json'},body:JSON.stringify({items:[{id:Number(this.dataset.giftId),quantity:1}],sections:['cart-drawer','cart-icon-bubble'],sections_url:location.pathname})});
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || labels.error);
        const verified = await fetch(root() + 'cart.js').then(response => response.json());
        const gift = verified.items.find(item => String(item.variant_id) === this.dataset.giftId);
        if (!gift || gift.final_line_price !== 0) {
          if (gift) await fetch(root() + 'cart/change.js', {method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({id:gift.key,quantity:0})});
          throw new Error(labels.giftNotApplied);
        }
        if (this.dataset.context === 'drawer' && document.querySelector('cart-drawer')?.renderContents) document.querySelector('cart-drawer').renderContents(result);
        else location.reload();
      } catch (error) { message.textContent = error.message || labels.error; }
      finally { if (this.isConnected) button.disabled = !this.querySelector('[data-dog-confirm]').checked; }
    }
    async quote() {
      const zip = this.querySelector('[data-shipping-zip]');
      const province = this.querySelector('[data-shipping-state]');
      const result = this.querySelector('[data-shipping-result]');
      const button = this.querySelector('[data-quote-shipping]');
      if (!/^\d{5}$/.test(zip.value) || !province.value) { result.textContent = labels.zipError; (!/^\d{5}$/.test(zip.value) ? zip : province).focus(); return; }
      this.abort?.abort();
      this.abort = new AbortController();
      const {signal} = this.abort;
      button.disabled = true;
      result.textContent = labels.quoting;
      const params = new URLSearchParams({'shipping_address[zip]':zip.value,'shipping_address[country]':'Mexico','shipping_address[province]':province.value});
      try {
        const fresh = await fetch(root() + 'cart.js', {signal}).then(response => response.json());
        if (!fresh.item_count) { result.textContent = labels.emptyCart; return; }
        const prepare = await fetch(root() + 'cart/prepare_shipping_rates.json?' + params, {method:'POST',signal});
        if (!prepare.ok) throw new Error(labels.quoteError);
        let rates;
        for (let attempt = 0; attempt < 10; attempt++) {
          await new Promise(resolve => setTimeout(resolve, 750));
          const response = await fetch(root() + 'cart/async_shipping_rates.json?' + params, {signal});
          if (!response.ok) throw new Error(labels.quoteError);
          const payload = await response.json();
          if (payload?.shipping_rates) { rates = payload.shipping_rates; break; }
        }
        result.replaceChildren();
        if (!rates?.length) result.textContent = labels.quoteError;
        else rates.forEach(rate => {
          const line = document.createElement('span');
          line.style.display = 'block';
          line.textContent = `${rate.presentment_name || rate.name}: ${money(Math.round(Number(rate.price) * 100))}`;
          result.appendChild(line);
        });
      } catch (error) { if (error.name !== 'AbortError') result.textContent = labels.quoteError; }
      finally { if (this.isConnected) button.disabled = false; }
    }
  }
  if (!customElements.get('prandy-mobile-purchase')) customElements.define('prandy-mobile-purchase', PrandyMobilePurchase);
  if (!customElements.get('prandy-cart-benefits')) customElements.define('prandy-cart-benefits', PrandyCartBenefits);
})();
